'use client'

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { 
  Database, 
  FileText, 
  Brain, 
  Building2, 
  Code2, 
  Lock, 
  Zap,
  Globe,
  Upload,
  MessageSquare,
  Calendar,
  CheckCircle2,
  ArrowRight
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

const CALENDLY_URL = 'https://calendly.com/govcondata/govcon-data-enterprise-demo'

const apiEndpoints = [
  {
    title: 'Federal Opportunities API',
    description: 'Access real-time federal contract opportunities from SAM.gov. Search, filter, and retrieve detailed solicitation data.',
    icon: Building2,
    features: [
      'Full SAM.gov contract database',
      'Search by NAICS, keywords, agency',
      'Real-time updates',
      'Historical data access',
    ],
  },
  {
    title: 'SLED Opportunities API',
    description: 'Query state, local, and education contract opportunities from all 50 states in a unified format.',
    icon: Globe,
    features: [
      'All 50 states consolidated',
      'Local government contracts',
      'Education sector (K-12 & Higher Ed)',
      'Standardized data format',
    ],
  },
  {
    title: 'Knowledge Base API',
    description: 'Upload and manage your company documents to build a custom knowledge base for AI-powered features.',
    icon: Upload,
    features: [
      'Document upload & indexing',
      'Support for PDF, DOCX, TXT',
      'Vector embeddings for semantic search',
      'Secure, isolated storage',
    ],
  },
  {
    title: 'AI Proposal Generator API',
    description: 'Generate proposal content programmatically using your knowledge base and our AI models.',
    icon: Brain,
    features: [
      'Custom proposal generation',
      'Uses your uploaded documents',
      'Multiple output formats',
      'Compliance-aware responses',
    ],
  },
]

const useCases = [
  {
    title: 'Internal Tools',
    description: 'Build custom dashboards and tools for your BD team',
    icon: Code2,
  },
  {
    title: 'CRM Integration',
    description: 'Sync opportunities directly to Salesforce, HubSpot, or custom CRM',
    icon: Database,
  },
  {
    title: 'AI Assistants',
    description: 'Power chatbots and AI tools with government contracting data',
    icon: MessageSquare,
  },
  {
    title: 'Automated Workflows',
    description: 'Trigger alerts, reports, and actions based on new opportunities',
    icon: Zap,
  },
]

export default function ApiDocsPage() {
  const [isCalendlyOpen, setIsCalendlyOpen] = useState(false)

  return (
    <div className="container mx-auto px-4 py-16">
      {/* Hero Section */}
      <div className="text-center mb-16">
        <Badge variant="secondary" className="mb-4">Enterprise API</Badge>
        <h1 className="text-4xl lg:text-5xl font-bold mb-6">
          GovCon Data API
        </h1>
        <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
          Enterprise-grade API access to federal and SLED government contract opportunities. 
          Build powerful integrations with comprehensive contract intelligence data and AI-powered proposal tools.
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <Button size="lg" onClick={() => setIsCalendlyOpen(true)}>
            <Calendar className="mr-2 h-5 w-5" />
            Schedule a Demo
          </Button>
          <Button size="lg" variant="outline" asChild>
            <a href="mailto:info@govcon-data.com">
              Contact Sales
            </a>
          </Button>
        </div>
      </div>

      {/* Who It's For */}
      <div className="mb-16 text-center">
        <h2 className="text-2xl font-semibold mb-4">Built for Developers & Enterprise Teams</h2>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Whether you're building internal tools for your BD team or integrating government contracting 
          data into your products, our API provides the foundation you need.
        </p>
      </div>

      {/* API Endpoints */}
      <section className="mb-20">
        <h2 className="text-3xl font-bold text-center mb-4">API Endpoints</h2>
        <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
          Comprehensive access to government contract data and AI-powered tools through RESTful endpoints.
        </p>
        <div className="grid md:grid-cols-2 gap-6">
          {apiEndpoints.map((endpoint) => (
            <Card key={endpoint.title} className="relative overflow-hidden">
              <CardHeader>
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-lg bg-primary/10">
                    <endpoint.icon className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">{endpoint.title}</CardTitle>
                    <CardDescription className="mt-2">
                      {endpoint.description}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {endpoint.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Use Cases */}
      <section className="mb-20">
        <h2 className="text-3xl font-bold text-center mb-4">Use Cases</h2>
        <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
          See how enterprise teams are using the GovCon Data API to streamline their operations.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {useCases.map((useCase) => (
            <Card key={useCase.title} className="text-center">
              <CardHeader>
                <div className="mx-auto p-3 rounded-full bg-primary/10 w-fit mb-2">
                  <useCase.icon className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="text-lg">{useCase.title}</CardTitle>
                <CardDescription>{useCase.description}</CardDescription>
              </CardHeader>
            </Card>
          ))}
        </div>
      </section>

      {/* Security & Compliance */}
      <section className="mb-20">
        <Card className="bg-muted/50">
          <CardContent className="py-12">
            <div className="text-center max-w-3xl mx-auto">
              <Lock className="h-12 w-12 text-primary mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-4">Enterprise-Grade Security</h2>
              <p className="text-muted-foreground mb-6">
                Your data is protected with industry-standard security measures. API keys are encrypted, 
                all connections use TLS 1.3, and your knowledge base documents are stored in isolated, 
                secure environments.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Badge variant="outline" className="py-2 px-4">
                  <Lock className="h-4 w-4 mr-2" />
                  TLS 1.3 Encryption
                </Badge>
                <Badge variant="outline" className="py-2 px-4">
                  <Database className="h-4 w-4 mr-2" />
                  Isolated Data Storage
                </Badge>
                <Badge variant="outline" className="py-2 px-4">
                  <FileText className="h-4 w-4 mr-2" />
                  SOC 2 Compliant Infrastructure
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Pricing CTA */}
      <section className="text-center">
        <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
          <CardContent className="py-12">
            <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
            <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
              Enterprise API access is available with custom pricing based on your needs. 
              Schedule a demo to discuss your requirements and see the API in action.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button size="lg" onClick={() => setIsCalendlyOpen(true)}>
                <Calendar className="mr-2 h-5 w-5" />
                Schedule a Demo
              </Button>
              <Button size="lg" variant="outline" asChild>
                <a href="mailto:info@govcon-data.com">
                  <ArrowRight className="mr-2 h-5 w-5" />
                  Contact info@govcon-data.com
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Calendly Modal */}
      <Dialog open={isCalendlyOpen} onOpenChange={setIsCalendlyOpen}>
        <DialogContent className="sm:max-w-[600px] h-[700px] p-0">
          <DialogHeader className="p-6 pb-2">
            <DialogTitle>Schedule an Enterprise Demo</DialogTitle>
            <DialogDescription>
              Book a call with our team to discuss enterprise API access and custom pricing.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 px-6 pb-6">
            <iframe
              src={`${CALENDLY_URL}?hide_gdpr_banner=1&hide_landing_page_details=1`}
              width="100%"
              height="100%"
              frameBorder="0"
              className="rounded-lg"
              style={{ minHeight: '580px' }}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
